/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  Dropdown,
  FormGroup,
  Link,
  PasswordInput,
  TextInput
} from '@carbon/react';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { SEARCH_AUTH_TYPES } from '../../../ts/skill';
import { SslNotification } from '../../ia/components/notifications/SslNotification';
import { generateDocsUrl } from '../../ia/util/docs';
import { REDACTED_FIELD } from '../../ia/util/string';
import { GRPC_URL_REGEX } from '../../ia/util/url';
import { validatePort } from '../../ia/util/validation/field-validation';
import {
  setAuthType,
  setPass,
  setUrl,
  setPort,
  setIsSetupValid,
  setUser,
  clearUser,
  clearPass,
  setIsDirty,
  setNoAuth
} from '../store/reducer';
import {
  selectDraftPass,
  selectDraftUrl,
  selectDraftPort,
  selectDraftUser,
  selectAuthType
} from '../store/selectors';
import { messages } from './messages';

import './MilvusInstanceConfig.scss';

interface MilvusInstanceConfigProps {
  noHeader?: boolean;
  readOnly: boolean;
  onClose: () => void;
  updateConfig: () => void;
}

const MILVUS_URL = generateDocsUrl(
  '/watson-assistant?topic=watson-assistant-search-milvus-add'
);

export const MilvusInstanceConfig = ({
  noHeader = false,
  readOnly,
  onClose,
  updateConfig
}: MilvusInstanceConfigProps) => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();

  const url = useSelector(selectDraftUrl);

  const [invalidUrl, setInvalidUrl] = useState(false);
  const [invalidUrlText, setInvalidUrlText] = useState('');
  const [invalidPort, setInvalidPort] = useState(false);

  const authType = useSelector(selectAuthType);
  const port = useSelector(selectDraftPort);
  const username = useSelector(selectDraftUser);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [invalidUsernameText, setInvalidUsernameText] = useState('');

  const password = useSelector(selectDraftPass);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [invalidPasswordText, setInvalidPasswordText] = useState('');

  const urlChange = e => {
    const url = e?.target?.value ?? '';

    if (url === null || _.isEmpty(url?.trim())) {
      setInvalidUrl(true);
      setInvalidUrlText(t(messages.invalidEmptyValueText));
    } else if (!url.match(GRPC_URL_REGEX)) {
      setInvalidUrl(true);
      setInvalidUrlText(t(messages.invalidUrlValueText));
    } else {
      setInvalidUrl(false);
      setInvalidUrlText('');
    }

    dispatch(setUrl(url));
  };

  const usernameChange = e => {
    const username = e?.target?.value ?? '';
    if (username === null || _.isEmpty(username?.trim())) {
      setInvalidUsername(true);
      setInvalidUsernameText(t(messages.invalidEmptyValueText));
    } else {
      setInvalidUsername(false);
      setInvalidUsernameText('');
    }

    dispatch(setUser(username));
  };

  const passwordChange = e => {
    const password = e?.target?.value ?? '';
    if (password === null || _.isEmpty(password?.trim())) {
      setInvalidPassword(true);
      setInvalidPasswordText(t(messages.invalidEmptyValueText));
    } else {
      setInvalidPassword(false);
      setInvalidPasswordText('');
    }
    dispatch(setPass(password));
  };
  const portChange = e => {
    const port = e?.target?.value ?? '';
    const isValidPort = validatePort(port);
    setInvalidPort(!isValidPort);
    dispatch(setPort(port));
  };

  const changeAuthType = e => {
    const authType = e?.selectedItem;
    dispatch(setAuthType(authType.id));

    if (authType.id === SEARCH_AUTH_TYPES.API) {
      dispatch(setUser('ibmlhapikey'));
      dispatch(clearPass());
      dispatch(setNoAuth(false));
    } else if (authType.id === SEARCH_AUTH_TYPES.BASIC) {
      dispatch(clearUser());
      dispatch(clearPass());
      dispatch(setNoAuth(false));
    } else {
      dispatch(clearUser());
      dispatch(clearPass());
      dispatch(setNoAuth(true));
      dispatch(setIsDirty(true));
    }
  };

  const items = [
    {
      id: SEARCH_AUTH_TYPES.BASIC,
      text: t(messages.authTypeBasicText)
    },
    {
      id: SEARCH_AUTH_TYPES.API,
      text: t(messages.authTypeAPIkeyText)
    },
    {
      id: SEARCH_AUTH_TYPES.NONE,
      text: t(messages.authTypeNoneText)
    }
  ];

  // Update the parent panel on the validity of the inputs for the custom service config.
  useEffect(() => {
    if (
      invalidUrl ||
      (authType === SEARCH_AUTH_TYPES.API && invalidPassword) ||
      (authType === SEARCH_AUTH_TYPES.BASIC &&
        (invalidUsername || invalidPassword))
    ) {
      dispatch(setIsSetupValid(false));
    } else if (url && password && username) {
      dispatch(setIsSetupValid(true));
    }
  }, [authType, dispatch, invalidPassword, invalidUrl, invalidUsername]);

  return (
    <>
      <div className="MilvusSearchInstanceConfig">
        <div
          className={cx('MilvusSearchInstanceConfig__content-container', {
            'MilvusSearchInstanceConfig__content-container-no-padding': noHeader
          })}>
          {!noHeader && (
            <>
              <div className="MilvusSearchInstanceConfig__header">
                <FormattedMessage {...messages.configHeading} />
              </div>
              <div className="MilvusSearchInstanceConfig__instruction">
                <FormattedMessage {...messages.description} />
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link--learn-more"
                  href={MILVUS_URL}>
                  <FormattedMessage {...messages.learnMore} />
                </Link>
              </div>
            </>
          )}
          <div
            className={cx({
              'MilvusSearchInstanceConfig__radio-button-padding': noHeader
            })}></div>

          <FormGroup legendText="" className="MilvusSearchInstanceConfig__form">
            <div
              className={cx({
                MilvusSearchInstanceConfig__URL: noHeader
              })}>
              <TextInput
                autoComplete="off"
                id="MilvusSearchInstanceConfig__form-url-input"
                invalid={invalidUrl}
                invalidText={invalidUrlText}
                placeholder={t(messages.urlPlaceholder)}
                readOnly={readOnly || noHeader}
                labelText={t(messages.urlLabelText)}
                onChange={urlChange}
                size="md"
                value={url}
                type="text"
              />
              {noHeader && (
                <Button
                  className="ElasticSearchInstanceConfig__update-button"
                  kind="secondary"
                  size="md"
                  onClick={updateConfig}>
                  <FormattedMessage {...messages.update} />
                </Button>
              )}
            </div>
            <SslNotification callback={onClose} />
            <TextInput
              autoComplete="off"
              id="MilvusSearchInstanceConfig__form-port-input"
              invalid={invalidPort}
              invalidText={t(messages.invalidPort)}
              placeholder={t(messages.portPlaceholder)}
              readOnly={readOnly || noHeader}
              labelText={t(messages.portLabelText)}
              onChange={portChange}
              size="md"
              value={port}
              type="text"
            />
            <br />
            <br />
            <Dropdown
              id="Milvus__form-auth-type-dropdown"
              label={t(messages.authTypeLabelText)}
              titleText={t(messages.authTypeLabelText)}
              items={items}
              readOnly={readOnly || noHeader}
              itemToString={item => item.text}
              selectedItem={items.find(item => {
                return item.id === authType;
              })}
              onChange={changeAuthType}
            />
            {authType === SEARCH_AUTH_TYPES.BASIC && (
              <TextInput
                autoComplete="off"
                id="MilvusSearchInstanceConfig__form-username-input"
                invalid={invalidUsername}
                invalidText={invalidUsernameText}
                placeholder={t(messages.usernamePlaceholder)}
                readOnly={readOnly || noHeader}
                labelText={t(messages.usernameLabelText)}
                onChange={usernameChange}
                size="md"
                value={username}
                type="text"
              />
            )}
            {authType !== SEARCH_AUTH_TYPES.NONE && (
              <PasswordInput
                autoComplete="new-password"
                id="MilvusSearchInstanceConfig__form-password-input"
                invalid={invalidPassword}
                invalidText={invalidPasswordText}
                placeholder={REDACTED_FIELD}
                readOnly={readOnly || noHeader}
                labelText={
                  authType === SEARCH_AUTH_TYPES.BASIC
                    ? t(messages.passwordLabelText)
                    : t(messages.apiKeyLabelText)
                }
                onChange={passwordChange}
                size="md"
                value={password}
              />
            )}
          </FormGroup>
        </div>
      </div>
    </>
  );
};
